/**
 * Responsibility: decide where a bridge crosses water, how wide its span is, and which way it faces.
 *
 * ## Finding a crossing without tracing anything
 *
 * A bridge has to sit across the water, square to it, and long enough to land on both banks. All three come from the
 * **gradient of the water depth**, which needed no new machinery: depth increases toward the middle of a channel, so its
 * gradient points across the water. That is the heading, for free.
 *
 * Reaching the water from a slot centre a few hundred units away is a Newton step — the depth deficit divided by the
 * local gradient is how far the water is. The profile is smooth, so one or two steps land on it. No path tracing, no
 * accumulated state, and the whole thing is a pure function of the slot index.
 *
 * The span then comes from walking out along that heading until the water ends on each side.
 *
 * ## One authoritative slot rule, consulted two ways
 *
 * `bridgeAt(slot)` is the only thing that knows whether a slot holds a bridge and what shape it is. Placement walks it
 * over a tile's slots; the clearance walks it over the slots near a query point. **Crucially it knows nothing about tile
 * ownership** — the search can carry a bridge's centre out of the slot that generated it, so a clearance test that
 * applied the tile rule would lose exactly those bridges. Ownership is applied by the collector alone.
 *
 * ## Why this module knows nothing about rivers
 *
 * It is handed a depth function and a water level. A lake would work identically; the reason lakes have no bridges is
 * that a bridge over a pond makes no sense, not that anything here would object.
 *
 * Contains no Three.js and no DOM access, so it is fully unit testable.
 */

import { createLocalRng, deriveSeed } from './rng.js';
import { archetypeForSpan } from './bridgeGeometry.js';
import { presence } from './waterSurface.js';
import { BRIDGE } from '../config.js';

/** Slot index a coordinate falls in. */
function slotAt(coordinate, spacing) {
  return Math.round(coordinate / spacing - 0.5);
}

/** Range of slot indices whose centres fall inside a span. */
function slotRange(start, length, spacing) {
  return {
    first: Math.ceil(start / spacing - 0.5),
    last: Math.ceil((start + length) / spacing - 0.5) - 1,
  };
}

/**
 * Build the shared slot rule.
 *
 * @param {number} seed - Base world seed.
 * @param {string} biomeId - Derives an independent stream.
 * @param {object} water
 * @param {(x: number, z: number) => number} water.ownDepthAt - Signed water depth, positive under water.
 * @param {(x: number, z: number) => number} water.waterLevelAt - Height of the water surface.
 * @param {(x: number, z: number) => number} water.guideAt - A field that falls toward the middle of the water and
 *   reaches 1 at the edge of the hollow holding it. **The search needs this and cannot use the depth.** Outside the
 *   hollow, depth is just "water level minus the hills", so its gradient follows the terrain rather than pointing at the
 *   channel — measured, a search on the depth alone found a crossing for only 3% of candidates because it wandered off in
 *   whatever direction the ground happened to fall.
 * @returns {Function} `(slotX, slotZ, sampleWeight, sampleGroundDetail?) => object|null`
 */
function createBridgeSlots(seed, biomeId, { ownDepthAt, waterLevelAt, guideAt }) {
  const bridgeSeed = deriveSeed(seed, `bridges:${biomeId}`);

  /**
   * Unit vector pointing toward the middle of the water, from the gradient of a field.
   *
   * Used on the guide field to walk *to* the water, and on the depth to square a bridge *across* it. Both gradients point
   * the same way once inside the hollow; only the guide is reliable outside it.
   */
  function descentAt(field, x, z) {
    const step = BRIDGE.GRADIENT_STEP;
    const gx = field(x + step, z) - field(x - step, z);
    const gz = field(x, z + step) - field(x, z - step);
    const length = Math.hypot(gx, gz);

    if (length < 1e-9) return null;
    return [gx / length, gz / length, length / (2 * step)];
  }

  /**
   * Walk from a starting point onto the water, if it is close enough.
   *
   * A Newton step on the guide field: the amount by which it exceeds the waterline, divided by its local gradient, is
   * roughly how far the water is. Each step is capped, so a nearly flat gradient cannot fling the search across the world.
   *
   * @returns {[number, number]|null} A point under water, or null.
   */
  function reachWater(startX, startZ) {
    let x = startX;
    let z = startZ;

    for (let step = 0; step < BRIDGE.SEARCH_STEPS; step += 1) {
      if (ownDepthAt(x, z) > 0) return [x, z];

      const guide = guideAt(x, z);
      const descent = descentAt(guideAt, x, z);
      if (!descent) return null;

      const [dx, dz, gradient] = descent;
      if (gradient < 1e-9) return null;

      // Down the guide, so *against* its gradient, toward the middle of the water.
      const distance = Math.min(BRIDGE.SEARCH_LIMIT, guide / gradient);
      x -= dx * distance;
      z -= dz * distance;
    }

    return ownDepthAt(x, z) > 0 ? [x, z] : null;
  }

  /** Unit vector across the water, from the depth gradient. Reliable once the search is standing in the water. */
  const acrossAt = (x, z) => descentAt(ownDepthAt, x, z);

  /**
   * How far the water extends either side of a point, along a heading.
   *
   * @returns {{back: number, forward: number}|null} Null if either waterline is beyond the probe limit.
   */
  function measureWater(x, z, dirX, dirZ) {
    const edges = [];

    for (const sign of [-1, 1]) {
      let found = null;

      for (let distance = BRIDGE.PROBE_STEP; distance <= BRIDGE.PROBE_LIMIT; distance += BRIDGE.PROBE_STEP) {
        if (ownDepthAt(x + dirX * sign * distance, z + dirZ * sign * distance) <= 0) {
          found = distance;
          break;
        }
      }

      // A crossing whose far bank is out of reach is not a river, and a bridge should not guess at it.
      if (found === null) return null;
      edges.push(found);
    }

    return { back: edges[0], forward: edges[1] };
  }

  return function bridgeAt(slotX, slotZ, sampleWeight, sampleGroundDetail) {
    const spacing = BRIDGE.SLOT_SPACING;
    const centerX = (slotX + 0.5) * spacing;
    const centerZ = (slotZ + 0.5) * spacing;

    /*
      **The same presence test the water surface uses, not merely a non-zero weight.** Water is only drawn where the owning
      biome's weight clears `WATER.MIN_WEIGHT`, and between zero and that floor a channel exists in the biome's own terrain
      while nothing is drawn — which is exactly how a bridge ended up standing on dry ground in a forest.
    */
    const weight = presence(sampleWeight(centerX, centerZ));
    if (weight <= 0) return null;

    /*
      Roll order is part of the world's identity. Reordering these, or inserting one before an existing one, moves every
      bridge at every seed. Append only.
    */
    const random = createLocalRng(bridgeSeed, slotX, slotZ);
    const presenceRoll = random();
    const jitterRoll = random();

    // Bridges are occasional, not a feature of every crossing.
    if (presenceRoll >= BRIDGE.DENSITY * weight) return null;

    /*
      Jitter is applied *before* the search rather than to the finished bridge, so a bridge always ends up square to the
      water wherever it lands. Nudging a finished one would leave it at an angle to the channel.
    */
    const offset = (jitterRoll * 2 - 1) * BRIDGE.SLOT_JITTER;
    const wet = reachWater(centerX + offset, centerZ + offset);
    if (!wet) return null;

    const across = acrossAt(wet[0], wet[1]);
    if (!across) return null;

    const [dirX, dirZ] = across;
    const measured = measureWater(wet[0], wet[1], dirX, dirZ);
    if (!measured) return null;

    // Centre the bridge on the water it crosses, not on wherever the search happened to land.
    const shift = (measured.forward - measured.back) / 2;
    const x = wet[0] + dirX * shift;
    const z = wet[1] + dirZ * shift;

    const waterWidth = measured.back + measured.forward;
    const span = waterWidth + 2 * BRIDGE.ABUTMENT;
    if (span < BRIDGE.MIN_SPAN || span > BRIDGE.MAX_SPAN) return null;

    /*
      **The weight has to be re-tested where the bridge actually ends up.** The search can carry a crossing a long way from
      the slot centre whose weight was checked, and measured it did: a bridge on seed 555 landed where the river's weight was
      *zero*, in lakeland, with the nearest drawn water 366 units off. Testing the crossing centre and both waterlines also
      stops a bridge straddling the presence contour with half its water undrawn.
    */
    if (presence(sampleWeight(x, z)) <= 0) return null;
    if (presence(sampleWeight(x - dirX * measured.back, z - dirZ * measured.back)) <= 0) return null;
    if (presence(sampleWeight(x + dirX * measured.forward, z + dirZ * measured.forward)) <= 0) return null;

    /*
      And the crossing has to stay in the neighbourhood of the slot that produced it. Four search steps of up to 700 units
      each could take a bridge nearly three thousand units away — far enough to leave the biome, and far enough that the
      clearance test's ring of neighbouring slots would not find it.
    */
    if (Math.hypot(x - centerX, z - centerZ) > BRIDGE.MAX_DRIFT) return null;

    const half = span / 2;
    const abutments = [
      [x - dirX * half, z - dirZ * half],
      [x + dirX * half, z + dirZ * half],
    ];

    const surface = waterLevelAt(x, z);
    let groundY = surface;
    let deckY = surface + BRIDGE.MIN_WATER_CLEARANCE;

    /*
      The ground is only available to the collector. The reservation is called from a context that has no terrain
      sampler, so it derives everything it can and skips the rest — which means it reserves space around a handful of
      bridges that were rejected for uneven banks. Over-reserving costs a tree; under-reserving puts one through a deck.
    */
    if (sampleGroundDetail) {
      const banks = abutments.map(([px, pz]) => sampleGroundDetail(px, pz).elevation);
      const lowest = Math.min(...banks);
      const highest = Math.max(...banks);

      // A bridge is level, so a crossing between mismatched banks is refused rather than tilted.
      if (highest - lowest > BRIDGE.MAX_BANK_SPREAD) return null;

      groundY = lowest;
      deckY = Math.max(highest + BRIDGE.BANK_RISE, surface + BRIDGE.MIN_WATER_CLEARANCE);
    }

    return {
      kind: 'bridge',
      archetype: archetypeForSpan(span),
      x,
      z,

      /*
        The heading runs *along* the deck, so it is the direction across the water. Taken from the depth gradient, which
        is what guarantees a bridge is square to what it crosses rather than at a random angle.
      */
      heading: Math.atan2(dirZ, dirX),

      span,
      waterWidth,
      deckY,
      groundY,
      abutments,
      weight,
    };
  };
}

/**
 * Build the bridge placement function.
 *
 * @param {number} seed - Base world seed.
 * @param {string} biomeId
 * @param {object} water - `{ ownDepthAt, waterLevelAt }`.
 * @returns {(region: object, context: object) => Array<object>}
 */
export function createBridges(seed, biomeId, water) {
  const bridgeAt = createBridgeSlots(seed, biomeId, water);

  return function collectStructures(region, { sampleWeight, sampleGroundDetail, isReserved }) {
    const spacing = BRIDGE.SLOT_SPACING;
    const xRange = slotRange(region.originX, region.size, spacing);
    const zRange = slotRange(region.originZ, region.size, spacing);

    const bridges = [];

    for (let slotX = xRange.first; slotX <= xRange.last; slotX += 1) {
      for (let slotZ = zRange.first; slotZ <= zRange.last; slotZ += 1) {
        const bridge = bridgeAt(slotX, slotZ, sampleWeight, sampleGroundDetail);
        if (!bridge) continue;

        // Keep clear of another biome's structures, tested at the centre and at both abutments.
        const blocked = [[bridge.x, bridge.z], ...bridge.abutments].some(([px, pz]) =>
          isReserved?.(px, pz, BRIDGE.DECK_WIDTH),
        );
        if (blocked) continue;

        /*
          Only the tile containing the bridge's centre builds it. The water search can carry that centre out of the slot
          it started from, so this is checked here rather than inside the slot rule — the clearance has to be able to see
          a bridge whichever tile ends up owning it.
        */
        if (bridge.x < region.originX || bridge.x >= region.originX + region.size) continue;
        if (bridge.z < region.originZ || bridge.z >= region.originZ + region.size) continue;

        bridges.push(bridge);
      }
    }

    return bridges;
  };
}

/**
 * Build the test that says whether a point falls inside a bridge's footprint.
 *
 * A circle over the whole crossing rather than a strip along the deck: the heading is not knowable without running the
 * search, and this has to answer cheaply at any coordinate. The same trade the arch clearance makes.
 *
 * @param {number} seed - Base world seed.
 * @param {string} biomeId
 * @param {object} water - `{ ownDepthAt, waterLevelAt }`.
 * @returns {(x: number, z: number, radius: number, context: object) => boolean}
 */
export function createBridgeClearance(seed, biomeId, water) {
  const bridgeAt = createBridgeSlots(seed, biomeId, water);

  return function nearABridge(x, z, radius, { sampleWeight, cache }) {
    const spacing = BRIDGE.SLOT_SPACING;
    const slotX = slotAt(x, spacing);
    const slotZ = slotAt(z, spacing);

    for (let dx = -1; dx <= 1; dx += 1) {
      for (let dz = -1; dz <= 1; dz += 1) {
        const key = `bridge:${slotX + dx},${slotZ + dz}`;
        let found = cache?.get(key);

        if (found === undefined) {
          /*
            Memoised per tile, and **not optional**. Finding a crossing costs dozens of depth samples, and without this
            every tree and tower in the world would pay for nine of those searches. The telescope array learned that the
            expensive way, at five milliseconds a forest tile.
          */
          found = bridgeAt(slotX + dx, slotZ + dz, sampleWeight) ?? null;
          cache?.set(key, found);
        }

        if (!found) continue;
        if (Math.hypot(x - found.x, z - found.z) < found.span / 2 + BRIDGE.CLEARANCE + radius) return true;
      }
    }

    return false;
  };
}
